// @ts-nocheck
import { object, date, string } from 'yup';
import moment from 'moment';

import { createYupSchema, createSafeStringValidator } from './Utils';
import {
  DATE_FORMAT,
  DEFAULT_TEXT_MAX_LENGTH_FALLBACK,
  EMPTY_ARRAY,
  EMPTY_OBJECT,
} from '../const/CommonConst';

const DESCRIPTION_TEXT_MAX_LENGTH = 1200;
const EXTENDED_TEXT_MAX_LENGTH = 10000;

const getDefaultTextMaxLength = (schemaOptions = EMPTY_OBJECT) => {
  const configuredMaxLength = Number(schemaOptions?.defaultTextMaxLength);
  return configuredMaxLength > 0
    ? configuredMaxLength
    : DEFAULT_TEXT_MAX_LENGTH_FALLBACK;
};

/**
 * Returns max length for text-like attributes.
 *
 * Why this exists:
 * - UI components (`TextFieldComponent` / `TextFieldExtendedComponent`) and schema
 *   validation must stay aligned to avoid mismatch between typing behavior and submit behavior.
 * - We intentionally centralize this mapping so all forms built from `createFormSchema`
 *   use the same limits.
 *
 * Rules:
 * - `Hyperlink` => unlimited (no max validation)
 * - `Character + TEXTFIELD_EXTENDED` => 10000
 * - `Character` with attribute name `description` => 1200
 * - all other `Character` => frontendConfig.defaultTextMaxLength (fallback 100)
 */
const getCharacterLimitForAttribute = (
  attribute = EMPTY_OBJECT,
  schemaOptions = EMPTY_OBJECT
) => {
  const dataType = attribute?.data_type;
  const inputControlType = attribute?.input_control_type;
  const attributeName = String(attribute?.name || '')?.toLowerCase();

  // Hyperlink has no character limit
  if (dataType === 'Hyperlink') {
    return undefined;
  }

  // Only apply character limits to Character data type
  if (dataType !== 'Character') {
    return undefined;
  }

  // Per-attribute override: if the attribute has its own text_field_char_limit, use it
  const attributeLevelLimit = Number(attribute?.text_field_char_limit);
  if (attributeLevelLimit > 0) {
    return attributeLevelLimit;
  }

  // Description fields get 1200 characters
  if (attributeName === 'description') {
    return DESCRIPTION_TEXT_MAX_LENGTH;
  }

  // Extended text fields (Character + TEXTFIELD_EXTENDED) get 10000 characters
  if (inputControlType === 'TEXTFIELD_EXTENDED') {
    return EXTENDED_TEXT_MAX_LENGTH;
  }

  // All other text fields: frontendConfig.defaultTextMaxLength or fallback 100
  return getDefaultTextMaxLength(schemaOptions);
};

/**
 * Applies text max-length validation constraints on top of an existing Yup form schema.
 *
 * Mirrors UI behavior:
 * - Hyperlink: no max length
 * - Character + TEXTFIELD_EXTENDED: 10000
 * - description: 1200
 * - all other text fields: frontendConfig.defaultTextMaxLength (fallback 100)
 *
 * @param {Object} existingSchema - The current Yup object schema
 * @param {Array} allAttributes - Array of all attributes used in the form
 * @returns {Object} New Yup object schema with character limits applied
 */
export const applyCharacterLimitConstraints = (
  existingSchema,
  allAttributes = EMPTY_ARRAY,
  schemaOptions = EMPTY_OBJECT
) => {
  // No schema or no attributes means there is nothing to decorate.
  if (!existingSchema || !allAttributes?.length) {
    return existingSchema;
  }

  const characterLimitOverrides = {};

  allAttributes.forEach((attribute) => {
    /**
     * IMPORTANT:
     * `createYupSchema` stores validators under `id ?? name`.
     * Some forms provide only `id`, others can rely on `name`.
     * Using the same key strategy prevents validator lookups from failing.
     */
    const schemaKey = attribute?.id ?? attribute?.name;
    const validator = existingSchema?.fields?.[schemaKey];
    const maxLength = getCharacterLimitForAttribute(attribute, schemaOptions);

    if (!schemaKey || !maxLength) {
      return;
    }

    // If validator exists and it is not a string validator, max-length is not applicable.
    if (validator && validator?.type !== 'string') {
      return;
    }

    /**
     * If the base schema has no validator for this field (common when `yup_schema` is empty),
     * create a fallback string validator so max-length still gets enforced at submit time.
     */
    let constrainedValidator =
      validator?.clone?.() || createSafeStringValidator(string());

    // Preserve nullable semantics from attribute metadata when creating fallback validators.
    if (attribute?.is_null) {
      constrainedValidator = constrainedValidator.nullable().default(null);
    }

    constrainedValidator = constrainedValidator.max(
      maxLength,
      `Maximum limit reached: ${maxLength} Characters`
    );

    if (constrainedValidator) {
      characterLimitOverrides[schemaKey] = constrainedValidator;
    }
  });

  if (Object.keys(characterLimitOverrides)?.length === 0) {
    return existingSchema;
  }

  // Merge all computed max-length validators into the existing schema in one pass.
  return existingSchema.shape(characterLimitOverrides);
};

/**
 * Creates a Yup validation schema for given attributes
 * @param {Array} attributes - Array of attribute objects
 * @returns {Object} Yup validation schema object
 */
export const createFormSchema = (attributes, schemaOptions = EMPTY_OBJECT) => {
  const schema = {};

  attributes?.forEach((attribute) => {
    const yupSchema = createYupSchema({}, attribute);
    if (yupSchema[attribute?.id]) {
      schema[attribute?.id] = yupSchema[attribute?.id];
    }
  });

  // Only create schema if we have attributes
  if (Object.keys(schema).length > 0) {
    // @ts-ignore - TypeScript doesn't know the dynamic schema shape
    const baseSchema = object().shape(schema);
    return applyCharacterLimitConstraints(
      baseSchema,
      attributes,
      schemaOptions
    );
  }

  return object();
};

/**
 * Creates initial form values for given attributes with their current values
 * @param {Array} attributes - Array of attribute objects
 * @param {Object} currentValues - Current attribute values
 * @returns {Object} Initial form values object
 */
export const createInitialFormValues = (
  attributes = EMPTY_ARRAY,
  currentValues = EMPTY_OBJECT
) => {
  const initialValues = { ...currentValues };

  attributes.forEach(({ id, data_type, name }) => {
    if (!id) return;

    const existingValue = initialValues[id];
    const isHealthAttribute = name === 'entity_health';

    const isAttValueEmpty =
      ((data_type === 'Paragraph' ||
        data_type === 'Document' ||
        data_type === 'AdvancedTable') &&
        (existingValue === '' ||
          existingValue === null ||
          existingValue === undefined ||
          (typeof existingValue === 'object' &&
            Object.keys(existingValue)?.length === 0))) ||
      (isHealthAttribute &&
        typeof existingValue === 'object' &&
        existingValue !== null &&
        Object.keys(existingValue)?.length === 0);

    const needsInitialization = !(id in initialValues) || isAttValueEmpty;

    if (!needsInitialization) return;

    switch (data_type) {
      case 'Checklist':
        initialValues[id] = {};
        break;
      case 'Document':
        initialValues[id] = [];
        break;
      case 'Paragraph':
        initialValues[id] = { title: '', description: '' };
        break;
      case 'AdvancedTable':
        initialValues[id] = { Cells: {} };
        break;
      default:
        initialValues[id] = isHealthAttribute ? '' : null;
        break;
    }
  });

  return initialValues;
};

/**
 * Applies date calendar constraints (min/max) to an existing Yup form schema.
 * Rebuilds the schema by overlaying .min() / .max() on date attributes that
 * have runtime constraints from the constraint API.
 *
 * @param {Object} existingSchema - The current Yup object schema
 * @param {Object} dateCalendarConstraints - Map of attributeId -> { min_date, max_date }
 * @returns {Object} New Yup object schema with date constraints applied
 */
export const applyDateCalendarConstraints = (
  existingSchema,
  dateCalendarConstraints
) => {
  if (
    !existingSchema ||
    !dateCalendarConstraints ||
    Object.keys(dateCalendarConstraints)?.length === 0
  ) {
    return existingSchema;
  }

  const dateOverrides = {};

  Object.keys(dateCalendarConstraints)?.forEach((attrId) => {
    const constraints = dateCalendarConstraints[attrId];

    // If constraints are null, reset the validator by removing min/max while preserving other validations
    if (!constraints) {
      if (existingSchema?.fields?.[attrId]) {
        const cleanedValidator = existingSchema?.fields?.[attrId]?.clone();
        // Remove only min and max tests, preserving required, typeError, etc.
        cleanedValidator.tests = cleanedValidator?.tests?.filter(
          (test) =>
            test?.OPTIONS?.name !== 'min' && test?.OPTIONS?.name !== 'max'
        );
        dateOverrides[attrId] = cleanedValidator;
      }
      return;
    }

    // Start with the existing field validator if present, otherwise create a new date validator
    let validator = existingSchema?.fields?.[attrId]
      ? existingSchema?.fields?.[attrId]?.clone()
      : date()
          ?.nullable()
          ?.default(null)
          ?.typeError('Please enter a valid date');

    // Apply min date constraint
    if (constraints?.min_date) {
      const minDateValue = moment(constraints?.min_date)
        ?.startOf('day')
        ?.toDate();
      const formattedMinDate = moment(constraints?.min_date)?.format(
        DATE_FORMAT
      );
      validator = validator?.min(
        minDateValue,
        `Date must not be before ${formattedMinDate}`
      );
    }

    // Apply max date constraint
    if (constraints?.max_date) {
      const maxDateValue = moment(constraints?.max_date)
        ?.endOf('day')
        ?.toDate();
      const formattedMaxDate = moment(constraints?.max_date)?.format(
        DATE_FORMAT
      );
      validator = validator?.max(
        maxDateValue,
        `Date must not be after ${formattedMaxDate}`
      );
    }

    dateOverrides[attrId] = validator;
  });

  // Only create new schema if there are overrides
  if (Object.keys(dateOverrides)?.length === 0) {
    return existingSchema;
  }

  // Merge the existing schema fields with the date overrides
  return existingSchema?.shape(dateOverrides);
};

/**
 * Applies mandatory constraints to an existing Yup form schema.
 * Rebuilds the schema by adding or removing required() validation on attributes
 * based on runtime constraints from the constraint API.
 *
 * @param {Object} existingSchema - The current Yup object schema
 * @param {Object} mandatoryConstraints - Map of attributeId -> boolean (true if mandatory)
 * @returns {Object} New Yup object schema with mandatory constraints applied
 */
export const applyMandatoryConstraints = (
  existingSchema,
  mandatoryConstraints,
  allAttributes = EMPTY_ARRAY
) => {
  if (
    !existingSchema ||
    !mandatoryConstraints ||
    Object.keys(mandatoryConstraints)?.length === 0
  ) {
    return existingSchema;
  }

  const mandatoryOverrides = {};

  Object.keys(mandatoryConstraints)?.forEach((attrId) => {
    const constraintValue = mandatoryConstraints[attrId];

    // If constraint is null/undefined, rebuild the original validator from the attribute's yup_schema
    if (constraintValue === null || constraintValue === undefined) {
      if (existingSchema?.fields?.[attrId] && allAttributes?.length > 0) {
        const originalAttribute = allAttributes.find(
          (attr) => attr.id?.toString() === attrId?.toString()
        );
        if (originalAttribute) {
          // Recreate the validator from the original attribute schema
          const freshSchema = createFormSchema([originalAttribute]);
          if (freshSchema?.fields?.[attrId]) {
            mandatoryOverrides[attrId] = freshSchema.fields[attrId];
          }
        }
      }
      return;
    }

    // It can be a boolean or an object { is_mandatory: boolean }
    const isMandatory =
      typeof constraintValue === 'object' && constraintValue !== null
        ? constraintValue.is_mandatory
        : constraintValue;

    if (existingSchema?.fields?.[attrId]) {
      let validator = existingSchema?.fields?.[attrId]?.clone();

      if (isMandatory === true) {
        // Add required validation
        validator = validator.required('This field is mandatory');
      } else if (isMandatory === false) {
        // Remove required validation properly
        validator = validator.notRequired().nullable();
      }
      mandatoryOverrides[attrId] = validator;
    }
  });

  // Only create new schema if there are overrides
  if (Object.keys(mandatoryOverrides)?.length === 0) {
    return existingSchema;
  }

  // Merge the existing schema fields with the mandatory overrides
  return existingSchema?.shape(mandatoryOverrides);
};
